import {
  useCallback,
  useEffect, useState
} from "react";
import { Accordion, Col, Container, Row } from "react-bootstrap";
import { useParams } from "react-router-dom";
import {
  promiseToast
} from "helpers/common";
import GoalTrackingForm from "./components/GoalTrackingForm";
import { getFirstGuardian } from "helpers/clients";
import AddNewGoalPop from "./components/Modals/AddNewGoal";
import { useCreateGoalTrackingMutation, useUpdateGoalTrackingMutation } from "store/services/clients/reports-v2/goals/trackGoals";
import { useDeleteGoalMutation } from "store/services/clients/reports-v2/goals/manageGoals";
import { useAutosaveReport } from "hooks/useAutosaveReport";
import { groupGoalTrackings } from "helpers/assessment";

const GoalsTracking = ({ data, formRefs, speechToText = false, onSubmit, refetch, clientType, chartRefs, error, isFetching }) => {
  const { triggerAutosave } = useAutosaveReport({
    onAutosave: onSubmit,
    isFetching: isFetching,
  });
  const { assessmentId, clientId } = useParams();

  const [assessmentVirtualChecked, setAssessmentVirtualChecked] = useState(false);
  const [submissionError, setSubmissionError] = useState(null);
  const [isSubmitting, setSubmitting] = useState(false);
  const [newGoalPopup, setNewGoalPopup] = useState(newGoalPopupState);
  const [activeKey, setActiveKey] = useState(0);

  const [deleteGoal, { isLoading: deleteGoalLoading }] = useDeleteGoalMutation();

  const [trackGoal, { isLoading: goalTrackLoading }] = useCreateGoalTrackingMutation();
  const [updateGoalTrack, { isLoading: goalTrackUpdateLoading }] = useUpdateGoalTrackingMutation();
  const assessmentGoals = data?.assesmentData?.reportGoals || [];
  const assessmentAssignedId = data?.assesment?.id;
  const goalsTrackingAdded = assessmentGoals?.every(x => x?.goalTracking?.length > 0);

  // useEffect(() => {
  //   refetch();
  // }, [refetch]);

  useEffect(() => {
    if (!assessmentGoals?.length) return;

    // Generate keys ["0", "1", "2", ...]
    const allKeys = assessmentGoals.map((_, index) => index);

    setActiveKey(allKeys);
  }, [assessmentGoals]);

  const saveGoalTrackings = useCallback(
    async (goalTrackings) => {
      try {
        // Persist the report form first so goal notes and tracker visibility
        // are saved before creating the tracker records.
        const reportSaved = await onSubmit?.();

        let payload = {
          goal_trackings: groupGoalTrackings(goalTrackings)
        };

        await promiseToast(
          trackGoal({
            assessmentId: assessmentAssignedId,
            data: payload,
            clientType
          }).unwrap(),
          { toastId: "createGoalTrack" }
        );

        await refetch();
        return true;
      } catch (err) {
        console.error("Error tracking goal", err);
        return false;
      }
    },
    [assessmentAssignedId, onSubmit]
  );

  const updateGoalTracking = useCallback(
    async (tracked, goalId, trackedId) => {
      try {
        let payload = {
          tracked,
          assesment_goal: goalId,
        };
        await promiseToast(
          updateGoalTrack({
            trackedId,
            assessmentId: assessmentAssignedId,
            data: payload,
            clientType
          }).unwrap(),
          { toastId: "updateGoalTrack" }
        );
        refetch();
      } catch (err) {
        console.error("Error updating goal tracking", err);
      }
    },
    [assessmentAssignedId]
  );

  const hideNewGoalPopup = () => setNewGoalPopup(newGoalPopupState);

  const openNewGoalPopup = (data) => {
    setNewGoalPopup({
      show: true,
      editId: data?.id || null,
      data: data || null,
    });
  };

  const handleRemoveGoal = async (goalId) => {
    try {
      // Save progression values from all goal forms before deletion/refetch.
      await onSubmit?.();

      await promiseToast(
        deleteGoal({ goalId, clientType }).unwrap(),
        { toastId: "removeGoal" }
      );
      const deletedAt = Date.now();
      // Refetch again if RTK returned onSubmit's in-flight, pre-delete request.
      const result = await refetch();
      if (result?.startedTimeStamp && result.startedTimeStamp < deletedAt) {
        await refetch();
      }
    } catch (err) {
      console.error("Error removing goal", err);
    }
  }

  return (
    <>
      <AddNewGoalPop
        showPopup={newGoalPopup?.show}
        closePopup={hideNewGoalPopup}
        data={newGoalPopup?.data}
        assessmentData={data}
        submitCallback={() => refetch()}
        clientType={clientType}
      />
      <section className="startSession position-relative py-3">
        <Container fluid>
          <Row>
            <>
              <Col lg="12" className="my-2">
                <div>
                  <h5 className="section-title mb-0">4.   GOALS</h5>
                  <Accordion onClick={(e) => e.stopPropagation()} activeKey={activeKey} onSelect={(key) => setActiveKey(key)} alwaysOpen>
                    {assessmentGoals?.length > 0 && (
                      assessmentGoals.map((goal, index) => {
                        return (
                          <GoalTrackingForm
                            speechToText={speechToText}
                            clientType={clientType}
                            key={goal?.id}
                            index={index}
                            innerRef={formRefs[index]}
                            goal={goal}
                            // handleTrackGoal={handleTrackGoal}
                            updateGoalTracking={updateGoalTracking}
                            saveGoalTrackings={saveGoalTrackings}
                            goalTrackLoading={goalTrackLoading}
                            removeGoal={handleRemoveGoal}
                            editGoal={openNewGoalPopup}
                            chartRef={(el) => (chartRefs.current[index] = el)}
                            autosaveTrigger={triggerAutosave}
                          />
                        );
                      })
                    )}
                  </Accordion>
                </div>
                <button
                  type="button"
                  className="blue-discharge-screen-btn proxima-regular"
                  // style={{
                  //   fontSize: "16px",
                  //   fontWeight: 500,
                  //   borderRadius: 2,
                  //   marginTop: "10px",
                  //   backgroundColor: "#315D99",
                  //   padding: '8px 22px',
                  //   border: 0
                  // }}
                  onClick={() => openNewGoalPopup()}
                >
                  Add an Area
                </button>
                {submissionError && (
                  <div className="py-2">
                    <div className="error">{submissionError}</div>
                  </div>
                )}
              </Col>
            </>
          </Row>
        </Container>
      </section>
    </>
  );
};

const newGoalPopupState = {
  show: false,
  data: null,
  editId: null,
};

export default GoalsTracking;
